import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, MessageSquare, HelpCircle, Book, ChevronDown, ChevronUp,
  Send, CheckCircle, ExternalLink, Loader2, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupportPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/api/email/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          subject: subject || 'Support Request',
          message,
          enquiry_type: subject,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send message');
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  const faqs = [
    {
      question: "How do I access the eBook after purchase?",
      answer: "After purchasing, you'll be automatically redirected to your library. You can also sign in anytime at /signin with the email you used during purchase, then go to /library to start reading."
    },
    {
      question: "Can I read on multiple devices?",
      answer: "Yes! Your eBook access works on any device. Just sign in with your account on web (any browser), or use our iOS and Android apps (coming soon). Your reading progress syncs automatically."
    },
    {
      question: "Is there a PDF download?",
      answer: "The eBook is designed to be read through our web reader or mobile apps for the best experience. We don't offer PDF downloads, but you can read anywhere with an internet connection."
    },
    {
      question: "How do I reset my password?",
      answer: "Go to /signin and click 'Forgot password'. We'll send you a link to reset it. If you don't receive the email, check your spam folder or contact support."
    },
    {
      question: "Can I get a refund?",
      answer: "If you're not satisfied with your purchase, contact us within 14 days for a full refund. We want you to be happy with your reading experience."
    },
    {
      question: "When will the mobile apps be available?",
      answer: "We're actively developing native iOS and Android apps. Sign up for notifications at /ios or /android to be the first to know when they launch."
    }
  ];

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#d4af37]/10 text-[#d4af37] rounded-full text-sm font-medium mb-4">
              Help & Support
            </span>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#2c3e50] font-display mb-4">
              How Can We Help?
            </h1>
            <p className="text-lg text-[#5a6c7d] max-w-2xl mx-auto">
              Find answers to common questions or get in touch with our support team.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-4">
            <Link 
              to="/library"
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-100 hover:border-[#d4af37] transition-colors"
            >
              <Book className="w-5 h-5 text-[#d4af37]" />
              <span className="text-[#2c3e50] font-medium">Go to Library</span>
            </Link>
            <Link 
              to="/read"
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-100 hover:border-[#d4af37] transition-colors"
            >
              <ExternalLink className="w-5 h-5 text-[#d4af37]" />
              <span className="text-[#2c3e50] font-medium">Web Reader</span>
            </Link>
            <Link 
              to="/apps"
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-100 hover:border-[#d4af37] transition-colors"
            >
              <HelpCircle className="w-5 h-5 text-[#d4af37]" />
              <span className="text-[#2c3e50] font-medium">Get the Apps</span>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-xl font-bold text-[#2c3e50] mb-6 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left"
                  data-testid={`faq-toggle-${index}`}
                >
                  <span className="font-medium text-[#2c3e50] pr-4">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#5a6c7d] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#5a6c7d] flex-shrink-0" />
                  )}
                </button>
                
                {expandedFaq === index && (
                  <div className="px-4 pb-4">
                    <p className="text-[#5a6c7d]">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#d4af37]/10 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-[#d4af37]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#2c3e50]">Contact Support</h2>
                <p className="text-sm text-[#5a6c7d]">We typically respond within 2 business days</p>
              </div>
            </div>

            {status === 'success' ? (
              <div className="text-center py-8" data-testid="support-success-message">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c3e50] mb-2">Message Sent!</h3>
                <p className="text-[#5a6c7d] mb-4">
                  Thanks for reaching out. We've sent a confirmation to your email and will respond within 2 business days.
                </p>
                <button
                  onClick={() => { setStatus('idle'); setName(''); setEmail(''); setSubject(''); setMessage(''); }}
                  className="text-[#d4af37] hover:text-[#c9a432] font-medium text-sm transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="support-form">
                {status === 'error' && (
                  <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="support-error-message">
                    <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
                    <p className="text-red-700 text-sm">{errorMsg}</p>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2c3e50] mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      disabled={status === 'loading'}
                      data-testid="support-name-input"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 disabled:opacity-50"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2c3e50] mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={status === 'loading'}
                      data-testid="support-email-input"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 disabled:opacity-50"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2c3e50] mb-1">
                    Subject
                  </label>
                  <select
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    disabled={status === 'loading'}
                    data-testid="support-subject-select"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 disabled:opacity-50"
                  >
                    <option value="">Select a topic</option>
                    <option value="Access Issues">Access Issues</option>
                    <option value="Payment & Billing">Payment & Billing</option>
                    <option value="Technical Problem">Technical Problem</option>
                    <option value="Feedback">Feedback</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2c3e50] mb-1">
                    Message
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    disabled={status === 'loading'}
                    rows={5}
                    data-testid="support-message-input"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 resize-none disabled:opacity-50"
                    placeholder="How can we help you?"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid="support-submit-button"
                  className="w-full flex items-center justify-center gap-2 bg-[#d4af37] text-[#2c3e50] px-6 py-4 rounded-xl font-semibold hover:bg-[#c9a432] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Alternative Contact */}
          <div className="mt-8 text-center">
            <p className="text-[#5a6c7d] text-sm">
              Or email us directly at{' '}
              <a 
                href="mailto:support@thirdcultureleadership.co.uk" 
                className="text-[#d4af37] hover:underline"
              >
                support@thirdcultureleadership.co.uk
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;
